import Decimal from 'decimal.js';
import type { ProductMarginRow } from '@erp/rpc';
import {
  calculateDiscountPercent,
  calculateTotal,
} from '@/pages/authenticated/sales-orders/components/line-item-table/priceUtils';

/**
 * One simulated order line. This is the entire persisted/working state for a
 * line — every display field (SKU, name, cost, sales history) is resolved at
 * render time from the margin query keyed by `productVariantId`, so a line
 * hydrated from the URL and one just added behave identically.
 */
export interface SimLine {
  /** Stable client-side key; survives reorder/remove/edit. Not persisted. */
  uid: string;
  productVariantId: string;
  /** User-entered unit selling price. */
  unitPrice: number;
  quantity: number;
}

/** Everything the table needs for one row, pre-computed. */
export interface DerivedRow {
  uid: string;
  productVariantId: string;
  sku: string | null;
  description: string | null;
  quantity: number;
  unitPrice: number;
  /** Oracle list price for the selected customer; null when no customer/price. */
  listPrice: number | null;
  /** Percent off list (0–100); null when no list price. */
  discountPct: number | null;
  totalPrice: number | null;
  /** Current standard cost (USD); null when margin data is missing. */
  cost: number | null;
  /** Margin percent (0–100 scale); null when cost missing or unit ≤ 0. */
  marginPct: number | null;
  /** (unit − prior-year ASP) × qty; null when no prior-year sales. */
  priceImpact: number | null;
  /** Raw margin row, used by the stacked ASP/Qty YoY cells. */
  marginRow: ProductMarginRow | undefined;
}

export interface Totals {
  totalRevenue: number;
  totalMargin: number;
  /** Blended margin percent (0–100); null when no costed revenue. */
  totalMarginPct: number | null;
  /** Blended discount-off-list percent (0–100); null when no list prices. */
  totalDiscountPct: number | null;
  totalPriceImpact: number;
}

function toFiniteNumber(
  value: string | number | null | undefined,
): number | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
}

/** Quantity-weighted ASP (USD) for a given year, or null. */
export function aspForYear(
  row: ProductMarginRow | undefined,
  year: number | undefined,
): number | null {
  if (row == null || year == null) return null;
  const entry = row.salesByYear.find((s) => s.year === year);
  return entry ? toFiniteNumber(entry.aspWeighted) : null;
}

/**
 * Compute every derived field for a single line. `prevYearAsp` is the ASP of
 * the year before the most recent covered year (yearsCovered[1]).
 */
export function deriveRow(
  line: SimLine,
  marginRow: ProductMarginRow | undefined,
  listPrice: number | null,
  prevYearAsp: number | null,
): DerivedRow {
  const { unitPrice, quantity } = line;
  const cost = toFiniteNumber(marginRow?.costUsd);
  const marginPct =
    cost != null && unitPrice > 0
      ? ((unitPrice - cost) / unitPrice) * 100
      : null;
  const priceImpact =
    prevYearAsp != null && prevYearAsp > 0 && unitPrice > 0 && quantity > 0
      ? (unitPrice - prevYearAsp) * quantity
      : null;

  return {
    uid: line.uid,
    productVariantId: line.productVariantId,
    sku: marginRow?.sku ?? null,
    description: marginRow?.description ?? marginRow?.productName ?? null,
    quantity,
    unitPrice,
    listPrice,
    discountPct: calculateDiscountPercent(unitPrice, listPrice),
    totalPrice: calculateTotal(quantity, unitPrice),
    cost,
    marginPct,
    priceImpact,
    marginRow,
  };
}

/**
 * Aggregate order-level totals from the derived rows. Mirrors the per-row math
 * (and `MarginTotalsSummary`) so the summary reconciles with the table, but
 * accumulates with Decimal to avoid float drift on monetary sums.
 */
export function deriveTotals(rows: DerivedRow[]): Totals {
  let totalRevenue = new Decimal(0);
  let costedRevenue = new Decimal(0);
  let totalMargin = new Decimal(0);
  let totalPriceImpact = new Decimal(0);
  let listRevenue = new Decimal(0);
  let discountedRevenue = new Decimal(0);

  for (const r of rows) {
    const unit = new Decimal(Number.isFinite(r.unitPrice) ? r.unitPrice : 0);
    const qty = new Decimal(Number.isFinite(r.quantity) ? r.quantity : 0);
    const lineRevenue = unit.times(qty);
    totalRevenue = totalRevenue.plus(lineRevenue);

    if (r.cost != null) {
      costedRevenue = costedRevenue.plus(lineRevenue);
      totalMargin = totalMargin.plus(unit.minus(r.cost).times(qty));
    }
    if (r.priceImpact != null) {
      totalPriceImpact = totalPriceImpact.plus(r.priceImpact);
    }
    if (r.listPrice != null && r.listPrice > 0) {
      listRevenue = listRevenue.plus(new Decimal(r.listPrice).times(qty));
      discountedRevenue = discountedRevenue.plus(lineRevenue);
    }
  }

  return {
    totalRevenue: totalRevenue.toNumber(),
    totalMargin: totalMargin.toNumber(),
    totalMarginPct: costedRevenue.gt(0)
      ? totalMargin.div(costedRevenue).times(100).toNumber()
      : null,
    totalDiscountPct: listRevenue.gt(0)
      ? Decimal.max(
          0,
          listRevenue.minus(discountedRevenue).div(listRevenue).times(100),
        ).toNumber()
      : null,
    totalPriceImpact: totalPriceImpact.toNumber(),
  };
}
